/**
 * Patient Card (EPIC PATIENT-CARD) — frozen closed vocabularies from the
 * FROZEN API CONTRACT (`state/epics/PATIENT-CARD/plan/build-plan.md` §1 +
 * §1.0.1 RECONCILE R1). Every card screen (F4–F8) reads THESE, never an
 * inline string literal — matches the repo's shared-constant convention
 * (see `constants/admission.js`).
 *
 * This module is CODE ONLY — no labels, no colours, no display copy (INV-13
 * / F3 scope). Screens own presentation; this is the vocabulary they bind to.
 */

// ── §1.2 closed section vocabulary — canonical order (BE and FE both
// hardcode this list; the server always emits `sections`/`requested_sections`
// in THIS order, never the caller's order, R1-2). ────────────────────────
export const SECTIONS = [
  'patient',
  'visits',
  'prescriptions',
  'clinical_notes',
  'procedures',
  'labs',
  'admissions',
  'documents',
  'timeline',
];

/**
 * Default section set (blank/omitted `?sections=` ⇒ this, R1-3) — DERIVED
 * from `SECTIONS` (never a second literal list) by excluding the ONE
 * off-by-default section (`clinical_notes`) the contract names.
 * Order is preserved from the canonical list.
 *
 * `payments` used to sit in this set too. It was CUT from the vocabulary
 * outright (owner D21) — it was never in the original request and entered
 * through the vocabulary rather than through a requirement, and the backend
 * now rejects it with 422 UNKNOWN_SECTION. It is removed from BOTH lists on
 * purpose: leaving a stale member here would still compute the right
 * `DEFAULT_SECTIONS` (it simply matches nothing), which is exactly why it
 * would have survived unnoticed as a lie about a section that does not exist.
 */
const OFF_BY_DEFAULT_SECTIONS = new Set(['clinical_notes']);
export const DEFAULT_SECTIONS = SECTIONS.filter((s) => !OFF_BY_DEFAULT_SECTIONS.has(s));

// ── §1.2 closed section status enum (6 values, closed — FE branches on
// this CODE, never on `items.length`). ────────────────────────────────────
export const SECTION_STATUS = {
  OK: 'OK',
  EMPTY: 'EMPTY',
  TRUNCATED: 'TRUNCATED',
  UNAVAILABLE: 'UNAVAILABLE',
  MODULE_DISABLED: 'MODULE_DISABLED',
  NOT_REQUESTED: 'NOT_REQUESTED',
};

/**
 * `reason_code` vocabulary (open-ended per §1.2 table; the four values named
 * in the contract). `NOT_IMPLEMENTED` (R1-7) is a DISTINCT, transitional
 * state from `LANE_READ_FAILED` — "not built yet" vs "the read genuinely
 * failed" — and must never be collapsed into one client-side error branch.
 */
export const SECTION_REASON_CODE = {
  IPD_MODULE_DISABLED: 'IPD_MODULE_DISABLED',
  LANE_READ_FAILED: 'LANE_READ_FAILED',
  PLAN_FANOUT_CAP: 'PLAN_FANOUT_CAP',
  NOT_IMPLEMENTED: 'NOT_IMPLEMENTED',
};

// ── §1.4 closed `doc_type` vocabulary. ────────────────────────────────────
export const DOC_TYPES = {
  INVOICE: 'INVOICE',
  DIAGNOSTICS_BILL: 'DIAGNOSTICS_BILL',
  DISCHARGE_SUMMARY: 'DISCHARGE_SUMMARY',
  DUES_ACK: 'DUES_ACK',
};

// ── §1.4 document `state` enum. ───────────────────────────────────────────
export const DOC_STATE = {
  INTERIM: 'INTERIM',
  DRAFT: 'DRAFT',
  FINAL: 'FINAL',
  CANCELLED: 'CANCELLED',
  NOT_ISSUED: 'NOT_ISSUED',
  NOT_AVAILABLE: 'NOT_AVAILABLE',
};

// ── §1.2 `TimelineRef.kind` closed vocabulary. ────────────────────────────
export const TIMELINE_KINDS = {
  VISIT: 'VISIT',
  ADMISSION: 'ADMISSION',
  LAB_ORDER: 'LAB_ORDER',
  PLAN: 'PLAN',
  SITTING: 'SITTING',
  PRESCRIPTION: 'PRESCRIPTION',
};

// ── §1.2 `labs[].context.kind` closed vocabulary. ─────────────────────────
export const LAB_CONTEXT_KIND = {
  OPD_VISIT: 'OPD_VISIT',
  IPD_ADMISSION: 'IPD_ADMISSION',
  UNLINKED: 'UNLINKED',
};

/**
 * `directory.resolved_mode` / query `mode` closed vocabulary (§1.3).
 * `AUTO` is the query default; `resolved_mode` on the response is always one
 * of the other three (the server never echoes `AUTO` back as resolved).
 */
export const DIRECTORY_MODE = {
  AUTO: 'AUTO',
  NAME: 'NAME',
  MOBILE: 'MOBILE',
  UHID: 'UHID',
};

/** `directory` query defaults (§1.3). */
export const DIRECTORY_QUERY_MIN_LENGTH = 3;
export const DIRECTORY_LIMIT_DEFAULT = 25;
export const DIRECTORY_LIMIT_MAX = 50;

/**
 * Timeline (F6) year-group long-list reveal (R10). Client-side only — the
 * whole `timeline` array is already in memory from the one aggregator call
 * (§1.2), so this is a REVEAL cap over an in-memory list, not a paginated
 * fetch. Reuses the SAME two numbers the cost-review named
 * (`DEFAULT_VISIT_LIMIT=20 / MAX_VISIT_LIMIT=50`, `tooth_chart_service.py:48`)
 * rather than inventing a new limit: 20 rows show per year initially / per
 * "Show N more" click, 50 is the hard ceiling ever rendered in one year group
 * (bounded even across repeated clicks).
 */
export const TIMELINE_YEAR_REVEAL_DEFAULT = 20;
export const TIMELINE_YEAR_REVEAL_MAX = 50;

/**
 * Timeline type-filter chip values (F6, mockup Variant A). A CLIENT-side view
 * filter over the already-resolved `timeline` rows — not a server vocabulary.
 */
export const TIMELINE_TYPE_FILTER = {
  ALL: 'ALL',
  OPD: 'OPD',
  IPD: 'IPD',
};

/**
 * Card / directory machine `error_code` vocabulary (§1.0/§1.1/§1.2/§1.3/§1.5
 * — the wire shape is `{"detail":{"error_code":"..."}}`, FE reads
 * `resp.data.detail.error_code`, never `detail.message` prose). Callers key
 * off THESE, never a free-hand string comparison.
 */
export const PATIENT_CARD_ERROR_CODE = {
  ROLE_REQUIRED: 'ROLE_REQUIRED',
  PATIENT_NOT_FOUND: 'PATIENT_NOT_FOUND',
  UNKNOWN_SECTION: 'UNKNOWN_SECTION',
  QUERY_TOO_SHORT: 'QUERY_TOO_SHORT',
  UNKNOWN_MODE: 'UNKNOWN_MODE',
  ADMISSION_NOT_FOUND: 'ADMISSION_NOT_FOUND',
  UNKNOWN_DOC_TYPE: 'UNKNOWN_DOC_TYPE',
  DOCUMENT_NOT_ISSUED: 'DOCUMENT_NOT_ISSUED',
  NO_LAB_CHARGES: 'NO_LAB_CHARGES',
  NO_DUES: 'NO_DUES',
};
